import { DESIGN_COLOR_PATTERN } from "@/lib/agent/colorProminence";

/**
 * Reconciles a free-text dress design description before it reaches the image
 * model.
 *
 * The create agent accumulates `brief.front.prompt` across a conversation by
 * appending each turn's contribution with a "; " separator. Over a multi-turn
 * design session that string collects three kinds of noise that must never
 * reach the (negation-blind) image model as positive tokens:
 *
 *   1. Rejections — "I hate black", "no strapless", "not too tight". Left
 *      intact these leak the rejected attribute ("black", "strapless") as a
 *      POSITIVE instruction. We drop the clause and lift the rejected subject
 *      into an explicit `avoid` list, which image models honor far better than
 *      a stray negated token.
 *   2. Conversational / process chatter — "ok generate the preview", "what do
 *      you recommend?", "current recommendation: ...". Not visual; drop it.
 *   3. Everything else is treated as genuine design intent and kept verbatim.
 *
 * This is a pure function so it can be unit-tested in isolation and composed
 * with the existing style-attribute sanitizers.
 */

export interface ReconciledDesignDescription {
  /** Cleaned, positive-only design description. */
  description: string;
  /** Attributes the user explicitly rejected, for negative guidance. */
  avoid: string[];
}

// Splits the accumulated prompt into candidate segments on both the "; "
// accumulator separator and sentence punctuation, so a single appended turn
// like "ok generate it. I hate black" becomes two independently-classifiable
// segments.
function splitIntoSegments(prompt: string): string[] {
  return prompt
    .split(/\s*;\s*|(?<=[.!?])\s+/)
    .map(segment => segment.trim())
    .filter(Boolean);
}

// Phrases that signal a segment is conversational/process text rather than a
// visual description of the garment.
const CHATTER_PATTERNS: RegExp[] = [
  /^current recommendation\s*:/i,
  /\b(?:generate|preview|render|create)\s+(?:the\s+)?(?:image|preview|design|it|this)\b/i,
  /\b(?:let'?s|lets)\s+(?:see|go|do|try)\b/i,
  /\b(?:are there|what do you|do you recommend|would you|could you|can you|which of these|what kind|any (?:other )?(?:suggestions?|options?|ideas?))\b/i,
  /\b(?:please )?(?:let me know|ready to preview|whenever you(?:'re| are) ready)\b/i,
  /\b(?:sounds?|looks?)\s+(?:good|great|perfect)\b/i,
  /^(?:ok(?:ay)?|sure|yes|yeah|yep|great|perfect|awesome|cool|thanks?|thank you)\b[\s.!,]*$/i,
  /\bi'?m\s+(?:happy|good|ready|excited|overwhelmed)\b/i,
];

// Leading rejection markers. The captured tail is the rejected subject that we
// route to the avoid list instead of the positive description.
const REJECTION_PATTERNS: RegExp[] = [
  /\bi\s+(?:really\s+)?(?:hate|dislike|don'?t\s+(?:like|want)|do\s+not\s+(?:like|want))\s+(.+)$/i,
  /\b(?:no|not|without|avoid|except|no\s+more)\s+(.+)$/i,
  /\b(?:instead\s+of|rather\s+than)\s+(.+)$/i,
];

// Trailing modifiers that are relative, not visual attributes — kept out of the
// avoid list so we don't emit "avoid: too tight" (nonsensical to the model).
const REJECTION_STOP_WORDS =
  /\b(too|very|so|really|that|the|a|an|any|of|it)\b/gi;

function normalizeAvoidSubject(raw: string): string {
  const cleaned = raw
    .replace(/[.!?,;]+$/g, "")
    .replace(REJECTION_STOP_WORDS, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return cleaned;
}

const EXPLICIT_AVOID_PATTERN =
  /\b(?:(?:i\s+)?(?:really\s+)?(?:hate|dislike)|(?:i\s+)?(?:do\s+not|don['’]?t|dont)\s+(?:want|like)|no(?:\s+more)?|without|avoid(?:ing)?|exclude(?:ing)?|omit(?:ting)?|remove(?:d|ing)?)\s+(?:(?:any|a|an|the)\s+)*(.+?)(?=\s*(?:[,;.!?\u2014\u2013]|$|\b(?:actually|on\s+second\s+thought|thinking\s+again|i\s+changed\s+my\s+mind|scratch\s+that)\b|\band\s+(?=(?:(?:i\s+)?(?:really\s+)?(?:hate|dislike)|(?:i\s+)?(?:do\s+not|don['’]?t|dont)\s+(?:want|like)|no(?:\s+more)?|without|avoid(?:ing)?|exclude(?:ing)?|omit(?:ting)?|remove(?:d|ing)?)\b)|\band\s+(?=(?:a|an|the|some)\s+)|\b(?:but|and|then|while)\s+(?=(?:keep(?:ing)?|retain(?:ing)?|preserv(?:e|ing)|leave|use|replace|add|include|make|with)\b)))/gi;

const NEVER_AVOID_PATTERN =
  /\bnever\s+(?:(?:want|use|include|choose|pick|wear|have)\s+)?(?:(?:any|a|an|the)\s+)*(.+?)(?=\s*(?:[,;.!?\u2014\u2013]|$|\b(?:actually|on\s+second\s+thought|thinking\s+again|i\s+changed\s+my\s+mind|scratch\s+that)\b|\bbut\b|\band\s+(?=(?:keep|use|replace|add|include|make|with)\b)))/gi;

// Hyphenated `non-*` modifiers are negative guidance too. Leaving
// "non-bridal" in a positive image description is especially dangerous for
// token-driven image models, which can emphasize the very bridal cue the user
// rejected. Keep this deliberately to one lexical attribute; broader phrases
// belong in the explicit no/without patterns above.
const NON_AVOID_PATTERN = /\bnon[-\s]+([\p{L}\p{N}][\p{L}\p{N}-]*)\b/giu;

const ALTERNATIVE_AVOID_PATTERN =
  /\b(?:other\s+than|anything\s+but|except|instead\s+of|rather\s+than)\s+(?:(?:any|a|an|the)\s+)*(.+?)(?=\s*(?:[,;.!?\u2014\u2013]|$|\b(?:but|then|while)\b|\band\s+(?=(?:keep|retain|preserve|leave|use|replace|add|include|make|with)\b)|\bwith\b))/gi;

const QUALIFIED_NOT_PATTERN =
  /\bnot\s+(?:(?:feel|be)\s+)?(?:(?:too|very|excessively|traditional(?:ly)?)\s+)*(itchy|scratchy|stiff|heavy|restrictive|hot|warm|revealing|tight|clingy|sheer|transparent|low[-\s]?cut|plunging|strapless|sparkly|sequined?|beaded|black|white|ivory|gold)\b/gi;

type AvoidPattern = {
  pattern: RegExp;
  kind: "explicit" | "never" | "non" | "alternative" | "qualified_not";
};

const AVOID_PATTERNS: readonly AvoidPattern[] = [
  { pattern: EXPLICIT_AVOID_PATTERN, kind: "explicit" },
  { pattern: NEVER_AVOID_PATTERN, kind: "never" },
  { pattern: NON_AVOID_PATTERN, kind: "non" },
  { pattern: ALTERNATIVE_AVOID_PATTERN, kind: "alternative" },
  { pattern: QUALIFIED_NOT_PATTERN, kind: "qualified_not" },
];

function normalizeExtractedAvoidRequirement(raw: string): string {
  return raw
    .replace(/^to\s+(?:use|include|wear|have|add)\s+/i, "")
    .replace(
      /\s+(?:for|because|so\s+that|in\s+order\s+to|(?:but|and|then|while)\s+(?:keep(?:ing)?|retain(?:ing)?|preserv(?:e|ing)|leave|use|replace|add|include|make|with))\s+.+$/i,
      ""
    )
    .replace(/\b(?:please|either)\s*$/i, "")
    .replace(/\s+/g, " ")
    .replace(/^[\s,;:.-]+|[\s,;:.-]+$/g, "")
    .trim()
    .toLowerCase();
}

/** Workflow objects are not visual features, including in previously saved
 * briefs. Keep concrete qualifiers such as "additional lace details" intact. */
export function isWorkflowAvoidRequirement(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    /^to\s+(?:refine|change|continue|proceed|preview|generate|render|decide|choose|try|wait|ask|spend)\b/i.test(
      normalized
    ) ||
    /^(?:additional|further|more|other|extra)\s+(?:details?|changes?|refinements?|questions?|options?|suggestions?|steps?)(?:\s+(?:yet|now|today|please|for\s+now|at\s+this\s+(?:point|stage)))?$/i.test(
      normalized
    )
  );
}

function isOperationalImageInstruction(value: string): boolean {
  return /\b(?:background|humans?|people|person|body\s+parts?|dress\s+stand|mannequin|watermarks?|labels?|written\s+content)\b/i.test(
    value
  );
}

/**
 * A negative word is not, by itself, a dress rejection. In particular, broad
 * `no ...` / `never ...` regexes also match conversational turns such as
 * "No problem", "Never mind", and "I never know which color works". Those
 * phrases must not become durable design memory or be partially erased from a
 * description.
 *
 * Keep this gate grammatical rather than a closed dress-attribute allowlist:
 * customers can reject arbitrary visual details ("never use feathers") that
 * the product has never seen before. We reject only subjects that clearly
 * describe conversation, uncertainty, history, or an operation instead of a
 * garment attribute.
 */
function isDesignAvoidRequirement(
  requirement: string,
  kind?: AvoidPattern["kind"]
): boolean {
  const normalized = requirement.trim().toLowerCase();
  if (
    !normalized ||
    isOperationalImageInstruction(normalized) ||
    isWorkflowAvoidRequirement(normalized)
  )
    return false;

  // Fixed conversational acknowledgements and vague pronouns are not visual
  // subjects. This also covers "No problem with satin", which expresses
  // acceptance rather than rejection of satin.
  if (
    /^(?:problem|problems|thanks?|thank\s+you|mind|worr(?:y|ies)|rush|pressure|need|preference|preferences|idea|ideas|clue|clues|way|ways|question|questions|issue|issues)(?:\b|$)/i.test(
      normalized
    ) ||
    /^(?:this|that|it|these|those|anything|something|everything|nothing)(?:\b|$)/i.test(
      normalized
    )
  ) {
    return false;
  }

  // A captured finite/experiential verb means `never` is describing the
  // customer's history or uncertainty, not a design veto. Explicit design
  // verbs such as "never use/include/wear" are consumed by
  // NEVER_AVOID_PATTERN before this gate, leaving their visual object here.
  if (
    /^(?:to\s+)?(?:do(?:ne|ing)?|did|know|knew|understand|understood|decid(?:e|ed|ing)|choos(?:e|ing)|chos(?:e|en)|tr(?:y|ied|ying)|been|was|were|had|made|design(?:ed|ing)|creat(?:e|ed|ing)|generat(?:e|ed|ing)|preview(?:ed|ing)?|render(?:ed|ing)?|refin(?:e|ed|ing)|chang(?:e|ed|ing)|continu(?:e|ed|ing)|proceed(?:ed|ing)?|spend(?:ing)?|wait(?:ed|ing)?|ask(?:ed|ing)?|realiz(?:e|ed|ing)|thought|think|seen|heard|used)(?:\b|$)/i.test(
      normalized
    )
  ) {
    return false;
  }

  // Interrogative/uncertainty clauses are especially likely after "no idea"
  // or "never know". A real avoidance is a noun/adjective phrase, not an
  // embedded question about what might suit or work.
  if (
    /\b(?:what|which|who|why|how|whether)\b/i.test(normalized) ||
    /\b(?:would|could|should|can|will)\s+(?:suit|work|look|fit|choose|pick|decide)\b/i.test(
      normalized
    )
  ) {
    return false;
  }

  // `non-*` is useful only as a visual modifier. Do not turn arbitrary process
  // adjectives (for example "non-refundable") into design memory. The
  // explicit forms above remain intentionally open-ended.
  if (
    kind === "non" &&
    !/\b(?:bridal|wedding|formal|casual|traditional|modern|classic|boho|romantic|dramatic|minimal(?:ist)?|modest|revealing|sheer|transparent|sparkly|sequined?|beaded|strapless|sleeveless|fitted|structured|flowy|floral|black|white|ivory|gold|matte|shiny)\b/i.test(
      normalized
    )
  ) {
    return false;
  }

  return true;
}

type AcceptedAvoidMatch = {
  start: number;
  end: number;
  requirement: string;
};

const DESIGN_CORRECTION_MARKER =
  /\b(?:actually|on\s+second\s+thought|thinking\s+again|i\s+changed\s+my\s+mind|scratch\s+that)\b/gi;

type DesignCorrection = { start: number; text: string };

function designCorrections(rawPrompt: string): DesignCorrection[] {
  DESIGN_CORRECTION_MARKER.lastIndex = 0;
  const markers = Array.from(rawPrompt.matchAll(DESIGN_CORRECTION_MARKER));
  return markers.map((marker, index) => ({
    start: marker.index ?? 0,
    text: rawPrompt
      .slice(
        (marker.index ?? 0) + marker[0].length,
        markers[index + 1]?.index ?? rawPrompt.length
      )
      .replace(/^[\s,;:\u2014\u2013-]+/, "")
      .trim(),
  }));
}

const POSITIVE_DESIGN_COMMITMENT =
  /\b(?:use|include|choose|pick|select|wear|keep|retain|restore|add|want|prefer|love|go\s+with|make)\b/i;

function isExplicitDesignCorrectionText(text: string): boolean {
  return (
    POSITIVE_DESIGN_COMMITMENT.test(text) &&
    !/\b(?:should|would|could|can)\s+i\b|\b(?:maybe|perhaps|not\s+sure|unsure|whether|what\s+if)\b/i.test(
      text
    )
  );
}

function requirementMentionPattern(requirement: string): RegExp | null {
  const words = requirement
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .match(/[a-z0-9]+/g);
  if (!words?.length) return null;
  return new RegExp(words.map(word => `\\b${word}\\b`).join("[\\s-]+"), "gi");
}

function correctionAffirmsRequirement(
  correction: DesignCorrection,
  requirement: string
): boolean {
  const mentionPattern = requirementMentionPattern(requirement);
  if (!mentionPattern) return false;
  mentionPattern.lastIndex = 0;
  for (const mention of correction.text.matchAll(mentionPattern)) {
    const mentionStart = mention.index ?? 0;
    const before = correction.text.slice(0, mentionStart);
    const localPrefix = before
      .split(/[,;.!?\u2014\u2013]|\b(?:but|then|instead|and)\b/i)
      .at(-1)
      ?.trim();
    const after = correction.text.slice(mentionStart + mention[0].length);
    if (!isExplicitDesignCorrectionText(correction.text)) continue;
    if (
      /\b(?:no|not|never|without|avoid(?:ing)?|exclude(?:ing)?|omit(?:ting)?|remove(?:d|ing)?|do\s+not|don['’]?t|dont)\b/i.test(
        localPrefix ?? ""
      )
    ) {
      continue;
    }
    if (
      POSITIVE_DESIGN_COMMITMENT.test(localPrefix ?? "") ||
      /^\s+(?:is|would\s+be)\s+(?:fine|okay|ok|right|good|preferred|what\s+i\s+want)\b/i.test(
        after
      )
    ) {
      return true;
    }
  }
  return false;
}

/** True when a later, explicitly corrective clause positively reselects the
 * same design requirement. Used by both generic and catalog-aware inference so
 * one turn cannot retain mutually exclusive "avoid X" and "use X" facts. */
export function hasLaterPositiveDesignCorrection(
  rawPrompt: string,
  requirement: string,
  afterIndex = -1
): boolean {
  return designCorrections(rawPrompt).some(
    correction =>
      correction.start > afterIndex &&
      correctionAffirmsRequirement(correction, requirement)
  );
}

/** The last direct correction is re-read independently by structured
 * inference, so a stale rejection earlier in the turn cannot hide its positive
 * replacement. */
export function latestExplicitDesignCorrection(
  rawPrompt: string
): string | null {
  return (
    [...designCorrections(rawPrompt)]
      .reverse()
      .find(correction => isExplicitDesignCorrectionText(correction.text))
      ?.text ?? null
  );
}

function recognizedAvoidMatches(rawPrompt: string): AcceptedAvoidMatch[] {
  const matches: AcceptedAvoidMatch[] = [];
  for (const { pattern, kind } of AVOID_PATTERNS) {
    pattern.lastIndex = 0;
    for (const match of rawPrompt.matchAll(pattern)) {
      const start = match.index ?? 0;
      let end = start + match[0].length;
      let subject = match[1] ?? "";
      // A final coordinator makes commas part of a shared rejection list:
      // "no slit, train, lace, or sequins". Bare comma clauses remain separate
      // so "no ruffles, long sleeves" does not negate the sleeve choice.
      if (
        ["explicit", "never", "alternative", "qualified_not"].includes(kind)
      ) {
        const continuation = rawPrompt
          .slice(end)
          .match(
            /^\s*,\s*(?:[^,;.!?\u2014\u2013]+,\s*)*?[^,;.!?\u2014\u2013]*?\b(?:and|or|nor)\s+[^,;.!?\u2014\u2013]+/i
          )?.[0]
          .split(
            /\s+(?:but|then|while)\b|\s+and\s+(?=(?:keep|retain|preserve|leave|use|replace|add|include|make|with)\b)/i
          )[0];
        if (continuation && isCoordinatedAvoidSubject(subject + continuation)) {
          subject += continuation;
          end += continuation.length;
        }
      }
      const subjects = isCoordinatedAvoidSubject(subject)
        ? coordinatedAvoidSubjects(subject)
        : [subject];
      for (const item of subjects) {
        const requirement = normalizeExtractedAvoidRequirement(
          item.replace(/^(?:any|a|an|the)\s+/i, "")
        );
        if (
          !requirement ||
          requirement.length > 160 ||
          !isDesignAvoidRequirement(requirement, kind)
        ) {
          continue;
        }
        matches.push({
          start,
          end,
          requirement,
        });
      }
    }
  }
  return matches;
}

const LEADING_AVOID_COLOR_PATTERN = new RegExp(
  `^(?:(?:light|dark|pale|bright|soft|deep|muted)[-\\s]+)?(?:${DESIGN_COLOR_PATTERN.source})`,
  "i"
);
const LEADING_AVOID_MODIFIER_PATTERN =
  /^(?:long|short|heavy|light|deep|high|low|tight|clingy|shiny|sparkly|metallic)\b/i;

function leadingAvoidModifier(value: string): string | undefined {
  return (
    value.match(LEADING_AVOID_COLOR_PATTERN)?.[0] ??
    value.match(LEADING_AVOID_MODIFIER_PATTERN)?.[0]
  );
}

function isAvoidModifier(value: string): boolean {
  return leadingAvoidModifier(value)?.length === value.length;
}

function sharedAvoidScope(value: string): string | undefined {
  const modifier = leadingAvoidModifier(value);
  if (!modifier) return undefined;
  // Match the full shade before looking for a noun. Otherwise "emerald green"
  // could be split into modifier "emerald" and a spurious garment noun "green".
  return value.slice(modifier.length).match(/^\s+(.+)$/)?.[1];
}

function coordinatedAvoidSubjects(subject: string): string[] {
  const items = subject.split(
    /\s*,\s*(?:(?:and|or|nor)\s+)?|\s+(?:and|or|nor)\s+/i
  );
  // In "no red, blue, or green lace", the trailing noun scopes every color.
  // Independent garment nouns ("slit, train, lace, or sequins") do not share
  // this structure and remain separate exclusions.
  const scope = sharedAvoidScope(items.at(-1)?.trim() ?? "");
  if (
    subject.includes(",") &&
    scope &&
    items.slice(0, -1).every(item => isAvoidModifier(item.trim()))
  ) {
    return items.map((item, index) =>
      index === items.length - 1 ? item : `${item.trim()} ${scope}`
    );
  }
  return items;
}

function isCoordinatedAvoidSubject(subject: string): boolean {
  if (!/\b(?:and|or|nor)\b/i.test(subject)) return false;
  const items = subject.split(
    /\s*,\s*(?:(?:and|or|nor)\s+)?|\s+(?:and|or|nor)\s+/i
  );
  const hasSharedModifier =
    !subject.includes(",") &&
    items.slice(0, -1).every(item => isAvoidModifier(item.trim())) &&
    !!sharedAvoidScope(items.at(-1)?.trim() ?? "");
  if (hasSharedModifier || /\bfit\s+and\s+flare\b/i.test(subject)) return false;
  return items.every(item => {
    const normalized = normalizeExtractedAvoidRequirement(item);
    // Coordination stops at a new positive instruction, an independent
    // rejection, or a finite clause. Do not widen shared modifiers such as
    // "black and white lace" or split the garment term "fit and flare".
    return (
      isDesignAvoidRequirement(normalized) &&
      (subject.includes(",") || !/^(?:a|an|the|some)\b/i.test(normalized)) &&
      !/^(?:with|without|no|not|never|avoid|remove|omit|exclude|keep|retain|preserve|leave|use|replace|add|include|make|i|we|it|this|that|they|there)\b/i.test(
        normalized
      ) &&
      !/\b(?:but|while|then|actually|is|are|was|were|should|would|could|want)\b/i.test(
        normalized
      )
    );
  });
}

/** Whether this occurrence belongs to an explicit negative scope, including
 * the non-leading members of a coordinated list. */
export function isInDesignAvoidanceScope(
  rawPrompt: string,
  start: number,
  end: number
): boolean {
  return recognizedAvoidMatches(rawPrompt).some(
    match => match.start <= start && match.end >= end
  );
}

function activeAvoidMatches(rawPrompt: string): AcceptedAvoidMatch[] {
  return recognizedAvoidMatches(rawPrompt).filter(
    match =>
      !hasLaterPositiveDesignCorrection(rawPrompt, match.requirement, match.end)
  );
}

/**
 * Extracts explicit negative design requirements without exposing negated
 * tokens as positive visual guidance to the image model. This complements the
 * canonical DesignSpec for legacy generation callers that do not yet send one.
 */
export function extractAvoidRequirements(rawPrompt: string): string[] {
  const results: string[] = [];
  const seen = new Set<string>();
  for (const match of activeAvoidMatches(rawPrompt)) {
    if (seen.has(match.requirement)) continue;
    seen.add(match.requirement);
    results.push(match.requirement);
  }
  return results;
}

/**
 * Removes negative clauses after they have been lifted into a dedicated avoid
 * list, preserving the positive clauses around an inline rejection.
 */
export function stripAvoidRequirementClauses(rawPrompt: string): string {
  let result = rawPrompt;
  const ranges = recognizedAvoidMatches(rawPrompt)
    .sort((left, right) => left.start - right.start || left.end - right.end)
    .reduce<Array<Pick<AcceptedAvoidMatch, "start" | "end">>>(
      (merged, match) => {
        const previous = merged[merged.length - 1];
        if (previous && match.start <= previous.end) {
          previous.end = Math.max(previous.end, match.end);
        } else {
          merged.push({ start: match.start, end: match.end });
        }
        return merged;
      },
      []
    )
    // Work backwards so indexes remain valid while slicing.
    .reverse();
  for (const match of ranges) {
    result = `${result.slice(0, match.start)}${result.slice(match.end)}`;
  }

  return result
    .replace(/,\s*,+/g, ", ")
    .replace(
      /\bwith\s+(?:but|and)\s+(?:keep(?:ing)?|retain(?:ing)?|preserv(?:e|ing)|leave|use)\s+/gi,
      "with "
    )
    .replace(/\s+(?:and|but)\s+with\s+/gi, " with ")
    .replace(
      /\s+(?:but|and|then)\s+(?:keep(?:ing)?|retain(?:ing)?|preserv(?:e|ing)|leave|use)\s+/gi,
      " "
    )
    .replace(/(^|[,.;])\s*(?:and|but)\s+/gi, "$1 ")
    .replace(/\b(?:and|but)\s*$/gi, "")
    .replace(/\s+(?=[,;.!?])/g, "")
    .replace(/^[\s,;:.\u2014\u2013-]+/g, "")
    .replace(/[\s,;:\u2014\u2013-]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isChatter(segment: string): boolean {
  const trimmed = segment.trim();
  // A model-authored visual prompt can accidentally echo the user's embedded
  // design question verbatim ("A navy gown. Which neckline works best?").
  // Questions are conversational decision text, never positive image-model
  // instructions. Keep this generic so new design fields do not need another
  // hand-maintained chatter phrase before they are safe.
  const isQuestion =
    /[?？]\s*$/.test(trimmed) ||
    /^(?:what|which|why|how|when|where|who|should|would|could|can|do|does|did|is|are|will)\b/i.test(
      trimmed
    );
  return isQuestion || CHATTER_PATTERNS.some(pattern => pattern.test(trimmed));
}

function extractRejection(segment: string): string | null {
  for (const pattern of REJECTION_PATTERNS) {
    const match = segment.match(pattern);
    if (match && match[1]) {
      const subject = normalizeAvoidSubject(match[1]);
      if (subject && isDesignAvoidRequirement(subject)) {
        return subject;
      }
    }
  }
  return null;
}

export function reconcileDesignDescription(
  rawPrompt: string
): ReconciledDesignDescription {
  const explicitAvoid = extractAvoidRequirements(rawPrompt ?? "");
  const segments = splitIntoSegments(
    stripAvoidRequirementClauses(rawPrompt ?? "")
  );
  const kept: string[] = [];
  const avoid: string[] = [...explicitAvoid];
  const seenAvoid = new Set(explicitAvoid);

  for (const segment of segments) {
    const rejection = extractRejection(segment);
    if (rejection) {
      if (!seenAvoid.has(rejection)) {
        seenAvoid.add(rejection);
        avoid.push(rejection);
      }
      continue;
    }
    if (isChatter(segment)) {
      continue;
    }
    // Drop the trailing sentence terminator left by the punctuation-aware
    // split so segments rejoin cleanly with the "; " separator.
    kept.push(segment.replace(/[.!?]+$/, "").trim());
  }

  return {
    description: kept.join("; "),
    avoid,
  };
}

/**
 * Convenience wrapper that folds the reconciled avoid list into a single
 * prompt string with an explicit negative-guidance clause, ready to hand to
 * `buildPromptWithSystem`.
 */
export function reconcileDesignPromptForImage(rawPrompt: string): string {
  const { description, avoid } = reconcileDesignDescription(rawPrompt);
  if (avoid.length === 0) {
    return description;
  }
  const avoidClause = `Avoid: ${avoid.join(", ")}.`;
  return description ? `${description}. ${avoidClause}` : avoidClause;
}
